import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  PushButton,
  VerticalProgress,
} from '../components';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import {
  Sound,
  Sounds,
} from '../../Sound';
import { OUTPUT_TTL } from '../../../constants/Pac';
import {
  InventoryLabel,
  TargetLabel,
  BalanceLabel,
  DeviceStatusLabel,
} from '../components/labels';
import {
  toNumber,
  zeroPad,
} from '../../../utils/numbers';
import convertUnit from '../../../utils/unit-converter';
import { PumpAccumulatorDisplayType } from '../../../constants/Configuration';
import Stopwatch from '../../Stopwatch/Stopwatch';
import {
  getRunMode,
  isRunModeNewYork,
} from '../../../stores/batch/run-mode';
import logger from '../../../utils/logger';
import { MeteredMaterialType } from '../../../constants/MaterialModels';
import InfoTooltip from '../../InfoTooltip';
import { timerKey as timerKeyUtil } from '../../../utils/timer-manager/timer-manager';

@inject('batchStore', 'materialStore', 'settingsStore')
@observer
class PumpBottleDevice extends Component {
  finishPumpDischarge() {
    const { device, batchStore } = this.props;
    const pumpStatus = batchStore.getDeviceStatus(device.id);
    const runningData = batchStore.running.find(v => v.currentDrop === pumpStatus.currentDrop && v.allDrops === pumpStatus.allDrops);

    if (runningData) {
      const pumpData = runningData.pumps[device.id];

      if (pumpData) {
        // Clear any running timers for this pump using timer manager
        batchStore.timers.clear(timerKeyUtil.device(device.id));
        logger.log(`Cleared timer for pump ${device.id}`);

        // Close the bottle gate if it's open
        if (device.bottleGatePNo) {
          batchStore.setOutput(device.bottleGatePNo, '0');
        }

        // Mark pump discharge as complete
        pumpData.isDischarged = true;

        // Remove from finish discharge devices list
        batchStore.finishDischargeDevices = batchStore.finishDischargeDevices.filter(id => id !== device.id);

        // Update actual weight with current discharge amount
        if (runningData && runningData.mixDesign) {
          runningData.mixDesign.forEach((v, i) => {
            if ((v.batchCode && v.batchCode === pumpData.material.batchCode) || v.constituentItemId === pumpData.material.itemId) {
              runningData.mixDesign[i].actualWeight = pumpStatus.weight;
              logger.log('discharge stopped - actual weight', pumpData.material.code, pumpStatus.weight, runningData.mixDesign[i]);
            }
          });
        }

        // Set device status to finish
        batchStore.deviceStatus.set(device.id, {
          ...batchStore.getDeviceStatus(device.id),
          error: false,
          status: DeviceStatus.FINISH,
        });

        logger.log(`pump ${device.description} manually finish discharge`, pumpData);
      }
    }
  }

  render() {
    const { device, batchStore, material, materialStore, settingsStore, onPush, onPushUp } = this.props;
    const { configuration } = settingsStore;
    const active = batchStore.isOutputOn(device.gatePNo);
    const isEmpty = batchStore.isPumpEmpty(device);
    const deviceStatus = batchStore.getDeviceStatus(device.id) || {};
    const { status, target, error, uid, tId, truckCode, currentDrop, allDrops, flowRate } = deviceStatus;
    const runMode = getRunMode(configuration, batchStore);
    const isError = error ? 'error' : '';
    const mCode = uid ? `${truckCode || tId || uid} ${currentDrop}/${allDrops}` : '';
    const md = material && materialStore ? materialStore.materialDetails[material.materialDetailId] || {} : {};
    const unit = md.itemBatchUnitSymbol || deviceStatus.unit;
    const inventoryUnit = material ? material.itemInventoryUnitSymbol : '';
    const weight = batchStore.getPumpWeight(device) * (md.meterRate || 1) || 0;
    const totalWeightInActiveLoad = batchStore.getTotalWeightInActiveLoad(device) || 0;
    const progress = deviceStatus.weight > 0 && (status !== DeviceStatus.FINISH && status !== DeviceStatus.IDLE)
      ? 100 - (((target - deviceStatus.weight) / target) * 100)
      : isEmpty ? 0 : 20;
    const tolerance = target ? batchStore.getToleranceByPercent(md, target) : 0;
    const currentBalance = material ? material.currentBalance : 0;
    const type = material ? materialStore.getMaterialName(material) : '';
    const inputDisabled = batchStore.isInputDisabled(device.emptyStatusPNo) ? 'device-input-disabled' : '';
    const isFeedMode = batchStore.isHoldFeedDevice(device.id) || [DeviceStatus.FEED, DeviceStatus.HOLD].includes(deviceStatus.status);
    const playOrPauseIcon = batchStore.isHoldFeedDevice(device.id) ? 'play' : 'pause';
    const deviceUnit = device.unitId ? batchStore.getUnitById(device.unitId) : null;
    const isDeferenceUnit = deviceUnit && unit ? unit !== deviceUnit : false;
    let convertedTarget;
    let convertedWeight;

    if (isDeferenceUnit) {
      convertedTarget = toNumber(convertUnit(target, deviceUnit, unit), 0);
      convertedWeight = toNumber(convertUnit(deviceStatus.weight, deviceUnit, unit), 0);
    }
    const flowRateTimeout = md.flowRateTimeout || 10;
    const canDisplayFlowRateCounter = flowRateTimeout && md.minimumFlowRate
      && batchStore.running.length && batchStore.canEmit && !batchStore.replayMode;

    // Use adjusted target from device status if available
    const targetToDisplay = deviceStatus.adjustedTargetForDisplay !== undefined ? deviceStatus.adjustedTargetForDisplay : target;

    return (
      <div id={`device-${device.id}`} className={`${active} status-${status} device-gui device-gui--bottle device-${isError} ${inputDisabled}`}>
        <Sound file={Sounds.alarm} play={isError} loop={true}/>
        <DeviceStatusLabel
          name={type}
          status={status}
          description={device.description}
          title={`#${device.id} ${inputDisabled}`}
          displayIcon={inputDisabled}
        />
        {canDisplayFlowRateCounter && (
          <Stopwatch
            start={status === DeviceStatus.FEED}
            stop={flowRate > md.minimumFlowRate || status !== DeviceStatus.FEED}
            delay={flowRateTimeout}
            countdown={true}
            hideIcon={true}
          />
        )}
        {!!md.showBalance && (
          <InventoryLabel balance={currentBalance} unit={inventoryUnit}/>
        )}
        <div className="device-icon body"/>
        <div className="device-icon water" style={{height: progress*0.42 + '%'}}/>
        <div className="device-label device-label-target">
          <TargetLabel
            target={targetToDisplay}
            unit={deviceUnit || unit}
            convertedTarget={isDeferenceUnit ? toNumber(convertUnit(targetToDisplay, deviceUnit, unit), 0) : convertedTarget}
            convertedUnit={unit}
            originalTarget={target}
            padZeros={md.meterRate > 1}
          />
          <BalanceLabel
            balance={toNumber(deviceStatus.weight, 2)}
            unit={deviceUnit || unit}
            convertedBalance={convertedWeight}
            convertedUnit={unit}
            padZeros={md.meterRate > 1}
          />
          {(settingsStore.configuration.pumpAccumulatorDisplayType === PumpAccumulatorDisplayType.ByLoad) ? (
            <>
              <div className="pac-balance">
                {md.meterRate > 1 ? zeroPad(totalWeightInActiveLoad, 2) : totalWeightInActiveLoad} {deviceUnit || unit}
                <InfoTooltip text={`Total ${type} pumped in current load`} />
              </div>
            </>
          ): (
            <div className="pac-balance">
              {md.meterRate > 1 ? zeroPad(weight, 2) : toNumber(weight, 2)} {deviceUnit || unit}
              <InfoTooltip text={`Current meter reading - shows total ${type} pumped since last login or last counter clear event`} />
            </div>
          )}
          {mCode && <div className="mix" title={mCode}>{mCode}</div>}
        </div>

        {(!!isFeedMode || isError) && !batchStore.isHoldFeed && !isRunModeNewYork(runMode) && (
          <button className={'btn-on btn-batch btn-batch-red btn-hold-feed'}
            title={batchStore.isHoldFeedDevice(device.id) ? 'Start feed' : 'Hold feed'}
            onClick={(e) => {
              if (!batchStore.canSendRemoteInstruction()) {
                e.stopPropagation();
              }
              batchStore.onPausePlayFeedButtonPress(device.id);
            }}>
            <i className={`fa fa-${playOrPauseIcon}`}/>
          </button>
        )}

        {!!isFeedMode && batchStore.isHoldFeedDevice(device.id) && !isRunModeNewYork(runMode) && (
          <button className={'btn-on btn-batch btn-batch-red btn-finish-feed'}
            title="Finish feed"
            onClick={(e) => {
              e.stopPropagation();
              batchStore.onFinishFeedButtonPress(device.id, material);
            }}>
            <i className={'fa fa-stop'}/>
          </button>
        )}

        {(status === DeviceStatus.DISCHARGING || status === DeviceStatus.DISCH_DELAY || status === DeviceStatus.LINE_CLEAR)
        && md.type === MeteredMaterialType.Admixture
        && !isRunModeNewYork(runMode) && (
          <button className={'btn-on btn-batch btn-batch-red btn-finish-discharge'}
            title="Stop discharge"
            onClick={(e) => {
              e.stopPropagation();
              batchStore.onFinishDischargeButtonPress(device.id, () => {
                this.finishPumpDischarge();
              });
            }}>
            <i className={'fa fa-stop'}/>
          </button>
        )}

        {device.meterPNo && !batchStore.running.length && (
          <button className="btn-batch btn-batch-red btn-reset-meter"
            title="Reset"
            onClick={(e) => {
              e.stopPropagation();
              batchStore.onPumpResetButtonClick(device);
            }}>
            <i className="fa fa-refresh"/>
          </button>
        )}

        {!configuration.manualFeed && batchStore.running.length ? '' : (
          <div className="device-buttons">
            {device.gatePNo && device.meterPNo && (
              <PushButton port={device.gatePNo}
                onPush={onPush}
                onPushUp={onPushUp}
                disabled={isRunModeNewYork(runMode)}
                onRightClickHoldLabel={'Hold to keep open..'}
                onRightClickHoldEnd={onPush}
                rightClickHoldDuration={1250}
              >
                <i className="fa fa-arrow-up"/>
              </PushButton>
            )}
            {device.bottleGatePNo && device.emptyStatusPNo && (
              <PushButton
                className="btn-batch btn-batch-red"
                port={device.bottleGatePNo}
                onPush={() => batchStore.setOutput(device.bottleGatePNo, OUTPUT_TTL)}
              />
            )}
          </div>
        )}

        <VerticalProgress
          showPercent={true}
          tolerance={tolerance}
          value={target > 0 ? (deviceStatus.weight / target) * 100 : 0}
          active={active}
        />
      </div>
    );
  }
}

export default PumpBottleDevice;
